/**
 * MarketingDashboardTab — Real Estate Marketing Dashboard, shown as an
 * extra tab on the Marketing HOD's dashboard. Tracks lead generation,
 * campaign performance, conversion funnel and agent handling of
 * marketing leads.
 *
 * Data: GET /api/hod/marketing-dashboard. CRM-backed sections (KPIs,
 * lead sources, funnel, campaigns, agents) show real aggregated data;
 * sections that need ad-platform / SEO / social integrations render a
 * clean "connect integration" state rather than fabricated numbers.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import HelpInfo from './HelpInfo';
import DirhamSign from './DirhamSign';
import {
  RefreshCw, TrendingUp, Users, Target, Megaphone, DollarSign,
  CheckCircle2, MapPin, Calendar, Filter, Zap, BarChart3, PlugZap,
} from 'lucide-react';
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, Tooltip, Legend, FunnelChart, Funnel, LabelList,
} from 'recharts';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString());

// Distinct, readable palette for the source / funnel charts.
const PALETTE = ['#7c3aed', '#2563eb', '#059669', '#d97706', '#dc2626',
  '#0891b2', '#db2777', '#65a30d', '#9333ea', '#0d9488', '#ea580c', '#4f46e5'];

const PERIODS = [
  { k: 'today', label: 'Today', days: 0 },
  { k: 'week', label: 'Weekly', days: 7 },
  { k: 'month', label: 'Monthly', days: 30 },
  { k: 'quarter', label: 'Quarterly', days: 90 },
  { k: 'year', label: 'Yearly', days: 365 },
];

const MarketingDashboardTab = () => {
  const { token } = useAuth();
  const [period, setPeriod] = useState('month');
  const [from, setFrom] = useState(daysAgoISO(30));
  const [to, setTo] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const applyPeriod = (p) => {
    setPeriod(p.k);
    setFrom(daysAgoISO(p.days));
    setTo(todayISO());
  };

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set('date_from', from);
      if (to) params.set('date_to', to);
      const res = await fetch(`${API}/api/hod/marketing-dashboard?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.success) setData(json.data);
      else toast.error(json.detail || json.error || 'Could not load marketing dashboard');
    } catch (e) {
      toast.error(`Load failed: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  }, [token, from, to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const kpis = data?.kpis || {};
  const leadSources = data?.lead_sources || [];
  const funnel = data?.funnel || [];
  const campaigns = data?.campaigns || [];
  const agents = data?.agents || [];

  // ---- KPI card definitions ----
  const kpiCards = useMemo(() => {
    const k = data?.kpis || {};
    return [
      { label: 'Total Leads Generated', value: fmtNum(k.total_leads), icon: Users, tone: 'violet' },
      { label: 'Qualified Leads', value: fmtNum(k.qualified_leads), icon: CheckCircle2, tone: 'emerald' },
      { label: 'Cost Per Lead (CPL)', value: <><DirhamSign /> {fmtNum(k.cpl)}</>, icon: DollarSign, tone: 'amber' },
      { label: 'Conversion Rate', value: `${k.conversion_rate ?? 0}%`, icon: TrendingUp, tone: 'blue' },
      { label: 'Active Campaigns', value: fmtNum(k.active_campaigns), icon: Megaphone, tone: 'violet' },
      { label: 'Marketing ROI', value: k.roi == null ? '—' : `${k.roi}%`, icon: TrendingUp, tone: 'emerald' },
      { label: 'Site Visits Generated', value: fmtNum(k.site_visits), icon: MapPin, tone: 'blue' },
      { label: 'Bookings from Marketing', value: fmtNum(k.bookings), icon: Target, tone: 'emerald' },
      { label: 'Total Ad Spend', value: <><DirhamSign /> {fmtNum(k.ad_spend)}</>, icon: DollarSign, tone: 'amber' },
      { label: 'Revenue from Marketing', value: <><DirhamSign /> {fmtNum(k.revenue)}</>, icon: DollarSign, tone: 'emerald' },
      { label: 'Lost Deals', value: fmtNum(k.lost_deals), icon: Target, tone: 'rose' },
      { label: 'Lead Response Time', value: '—', icon: Calendar, tone: 'slate', note: 'Needs SLA tracking' },
    ];
  }, [data]);

  const toneText = {
    violet: 'text-violet-600', emerald: 'text-emerald-600', amber: 'text-amber-600',
    blue: 'text-blue-600', rose: 'text-rose-600', slate: 'text-slate-500',
  };

  // ---- chart data ----
  const sourcePie = leadSources.slice(0, 12).map((s) => ({ name: s.source, value: s.leads }));
  const sourceBar = leadSources.slice(0, 10).map((s) => ({ name: s.source, Qualified: s.qualified, Leads: s.leads }));
  const funnelData = funnel.map((f, i) => ({ name: f.stage, value: f.count, fill: PALETTE[i % PALETTE.length] }));

  // A section card used for the integration-dependent panels — honest
  // empty state instead of fake metrics.
  const IntegrationCard = ({ title, items, note }) => (
    <Card className="border-slate-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <PlugZap size={15} className="text-slate-400" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {items.map((it) => (
            <Badge key={it} variant="outline" className="text-[11px] text-slate-500">{it}</Badge>
          ))}
        </div>
        <p className="text-xs text-slate-400">{note}</p>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-5">
      {/* Header + filters */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                <BarChart3 size={18} className="text-violet-600" />
                Marketing Dashboard
                <HelpInfo
                  align="inline"
                  title="How to use the Marketing Dashboard"
                  steps={[
                    'A real-time view of lead generation, campaign performance, the conversion funnel and how agents handle marketing leads.',
                    'The KPI cards up top summarise the period — leads, qualified leads, CPL, conversion, ROI, site visits, bookings, ad spend and revenue.',
                    'Use the period buttons (Today / Weekly / Monthly / Quarterly / Yearly) or the date pickers to scope every section at once.',
                    <>Lead Sources, the Funnel, Campaigns and Agent Performance are live CRM data. Sections marked with a <strong>plug icon</strong> light up once the matching ad-platform / SEO / social integration is connected.</>,
                  ]}
                  tip="Watch CPL against conversion — a cheap source that doesn't convert is worse than an expensive one that does."
                />
              </CardTitle>
              <CardDescription>
                Lead generation, campaign ROI and conversion funnel — Marketing department
              </CardDescription>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={fetchData} title="Refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={14} className="text-slate-400" />
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
              {PERIODS.map((p) => (
                <button
                  key={p.k}
                  type="button"
                  onClick={() => applyPeriod(p)}
                  className={`px-3 py-1.5 text-xs transition-colors ${
                    period === p.k ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <Calendar size={14} className="text-slate-400 ml-1" />
            <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPeriod(''); }}
              className="h-8 text-xs border border-slate-300 rounded px-2" />
            <span className="text-xs text-slate-400">to</span>
            <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPeriod(''); }}
              className="h-8 text-xs border border-slate-300 rounded px-2" />
          </div>
        </CardContent>
      </Card>

      {/* 1. KPI summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {kpiCards.map((c) => {
          const Icon = c.icon;
          return (
            <Card key={c.label} className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="text-xs text-slate-500">{c.label}</p>
                    <p className={`text-xl font-semibold mt-1 ${toneText[c.tone] || 'text-slate-900'}`}>
                      {c.value}
                    </p>
                    {c.note && <p className="text-[10px] text-slate-400 mt-0.5">{c.note}</p>}
                  </div>
                  <Icon size={18} className={`${toneText[c.tone] || 'text-slate-400'} opacity-70 flex-shrink-0`} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* 2. Lead Source Analytics */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 flex-wrap">
            <Users size={16} className="text-violet-600" /> Lead Source Analytics
            <HelpInfo
              align="inline"
              title="How to read Lead Source Analytics"
              steps={[
                'Every channel that brought in leads for the selected period — portals, ad platforms, website, referrals, walk-ins.',
                'The pie chart shows the share of total leads by source; the bar chart compares total Leads vs Qualified side by side.',
                <>The table\'s <strong>Qualify %</strong> is qualified leads ÷ total leads for that source — the real quality signal.</>,
                'A source can send lots of leads but qualify few — that\'s cheap volume, not value. Sort your attention by Qualify %, not Leads.',
              ]}
              tip="Shift budget toward the sources with the highest Qualify %, even if their raw lead count is lower."
            />
          </CardTitle>
          <CardDescription>Where your leads come from, and how well each source qualifies</CardDescription>
        </CardHeader>
        <CardContent>
          {leadSources.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">No lead data for this period.</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={sourcePie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85}>
                      {sourcePie.map((e, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sourceBar} margin={{ top: 8, right: 8, bottom: 40, left: 0 }}>
                    <XAxis dataKey="name" angle={-35} textAnchor="end" interval={0} height={60} tick={{ fontSize: 9 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar dataKey="Leads" fill="#c4b5fd" />
                    <Bar dataKey="Qualified" fill="#7c3aed" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {leadSources.length > 0 && (
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-500">
                    <th className="text-left py-2 px-3 font-medium">Source</th>
                    <th className="text-center py-2 px-3 font-medium">Leads</th>
                    <th className="text-center py-2 px-3 font-medium">Qualified</th>
                    <th className="text-center py-2 px-3 font-medium">Qualify %</th>
                  </tr>
                </thead>
                <tbody>
                  {leadSources.map((s) => (
                    <tr key={s.source} className="border-b border-slate-100">
                      <td className="py-2 px-3 font-medium text-slate-800">{s.source}</td>
                      <td className="py-2 px-3 text-center">{s.leads}</td>
                      <td className="py-2 px-3 text-center">{s.qualified}</td>
                      <td className="py-2 px-3 text-center">
                        <Badge variant="outline" className="text-xs">{s.conversion}%</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 3. Conversion Funnel + 4. Campaign Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
              <TrendingUp size={16} className="text-emerald-600" /> Conversion Funnel
              <HelpInfo
                align="inline"
                title="How to read the Conversion Funnel"
                steps={[
                  'The journey a marketing lead takes: Leads Generated → Qualified Leads → Site Visits → Negotiation → Bookings.',
                  'Each band\'s width is its volume — the number on the left. A sharp narrowing between two stages is where leads are dropping off.',
                  <>A big fall from <em>Leads Generated</em> to <em>Qualified</em> means lead quality or follow-up speed is weak; a fall from <em>Site Visits</em> to <em>Bookings</em> points to a closing problem.</>,
                  'Compare the funnel across periods using the date filter to see whether a fix is working.',
                ]}
                tip="Fix the widest drop-off first — that single stage is capping every number below it."
              />
            </CardTitle>
            <CardDescription>Leads → Qualified → Site Visits → Negotiation → Bookings</CardDescription>
          </CardHeader>
          <CardContent>
            {funnelData.every((f) => !f.value) ? (
              <p className="text-sm text-slate-500 text-center py-8">No funnel data for this period.</p>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <FunnelChart>
                    <Tooltip />
                    <Funnel dataKey="value" data={funnelData} isAnimationActive>
                      <LabelList position="right" fill="#334155" stroke="none" dataKey="name" style={{ fontSize: 11 }} />
                      <LabelList position="left" fill="#334155" stroke="none" dataKey="value" style={{ fontSize: 11 }} />
                    </Funnel>
                  </FunnelChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
              <Megaphone size={16} className="text-amber-600" /> Campaign Performance
              <HelpInfo
                align="inline"
                title="How to read Campaign Performance"
                steps={[
                  'Every marketing campaign — email campaigns and ad campaigns — with its platform, current status and lead count.',
                  <>Status shows where the campaign is: <em>Draft</em>, <em>Scheduled</em>, <em>Active</em>, <em>Completed</em>. Only active ones count toward the "Active Campaigns" KPI.</>,
                  'Leads is how many leads the campaign has produced so far — 0 on a Draft is expected, 0 on an Active one is a red flag.',
                  'Detailed per-platform spend, CTR and CPL appear in the Meta / Google Ads panels once those accounts are connected.',
                ]}
                tip="An Active campaign sitting at 0 leads after a few days usually means the targeting or creative needs a rework."
              />
            </CardTitle>
            <CardDescription>Email + ad campaigns and their status</CardDescription>
          </CardHeader>
          <CardContent>
            {campaigns.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">No campaigns found.</p>
            ) : (
              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-500">
                      <th className="text-left py-2 px-2 font-medium">Campaign</th>
                      <th className="text-left py-2 px-2 font-medium">Platform</th>
                      <th className="text-center py-2 px-2 font-medium">Status</th>
                      <th className="text-center py-2 px-2 font-medium">Leads</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((c, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="py-2 px-2 font-medium text-slate-800 truncate max-w-[160px]" title={c.name}>{c.name}</td>
                        <td className="py-2 px-2 text-slate-600">{c.platform}</td>
                        <td className="py-2 px-2 text-center">
                          <Badge variant="outline" className="text-[11px] capitalize">{c.status}</Badge>
                        </td>
                        <td className="py-2 px-2 text-center">{c.leads || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 6. Agent Performance from Marketing Leads */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 flex-wrap">
            <Users size={16} className="text-blue-600" /> Agent Performance from Marketing Leads
            <HelpInfo
              align="inline"
              title="How to read Agent Performance"
              steps={[
                'One row per marketing agent, showing how they handle the leads they\'re assigned.',
                <><strong>Leads Assigned</strong> is how many contacts they own; <strong>Site Visits</strong> is field visits they logged; <strong>Closings</strong> is deals they won.</>,
                'Read it as a mini-funnel per agent — lots of leads but few site visits means weak follow-through; site visits but no closings means a closing-skill gap.',
                'Pair this with the Lead Source table — an agent fed poor-quality leads will underperform through no fault of their own.',
              ]}
              tip="Before judging an agent, check whether their leads came from a high or low Qualify % source."
            />
          </CardTitle>
          <CardDescription>How the marketing team handles the leads they're assigned</CardDescription>
        </CardHeader>
        <CardContent>
          {agents.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">No marketing agents found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-500">
                    <th className="text-left py-2 px-3 font-medium">Agent</th>
                    <th className="text-center py-2 px-3 font-medium">Leads Assigned</th>
                    <th className="text-center py-2 px-3 font-medium">Site Visits</th>
                    <th className="text-center py-2 px-3 font-medium">Closings</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((a, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-2 px-3 font-medium text-slate-800">{a.name}</td>
                      <td className="py-2 px-3 text-center">{a.leads_assigned}</td>
                      <td className="py-2 px-3 text-center">{a.site_visits}</td>
                      <td className="py-2 px-3 text-center">{a.closings}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Integration-dependent sections — honest "connect to populate" cards */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Zap size={15} className="text-slate-400" />
          <h4 className="text-sm font-semibold text-slate-600">Connect integrations to unlock</h4>
          <HelpInfo
            align="inline"
            title="About these sections"
            steps={[
              'These panels need an external data source the CRM doesn\'t collect on its own — ad platforms, search analytics, social accounts, or messaging providers.',
              <>Each card lists the metrics it will show — e.g. <em>Meta Ads</em> brings Impressions, CTR, CPC, CPL once the Meta account is linked.</>,
              'Until a source is connected, the card stays as a labelled placeholder rather than showing fabricated numbers.',
              'Connecting an integration is an admin/settings action — once done, the matching panel fills with live data automatically.',
            ]}
            tip="Prioritise connecting Meta and Google Ads first — that unlocks real CPL and ROI per campaign."
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <IntegrationCard
            title="Meta Ads Analytics"
            items={['Impressions', 'Reach', 'CTR', 'CPC', 'CPL', 'Conversions']}
            note="Connect the Meta Ads account to stream live ad metrics here."
          />
          <IntegrationCard
            title="Google Ads Analytics"
            items={['Search clicks', 'Display clicks', 'Keyword performance', 'Landing page conv.']}
            note="Connect the Google Ads account to populate this panel."
          />
          <IntegrationCard
            title="SEO Analytics"
            items={['Organic traffic', 'Top keywords', 'Property page ranking']}
            note="Link a Search Console / analytics source to track organic performance."
          />
          <IntegrationCard
            title="WhatsApp & Email Campaigns"
            items={['Sent', 'Delivered', 'Read', 'Open rate', 'Click rate', 'Bounce rate']}
            note="Per-message delivery analytics appear once the messaging provider reports back."
          />
          <IntegrationCard
            title="Social Media Dashboard"
            items={['Instagram', 'Facebook', 'TikTok', 'LinkedIn', 'YouTube', 'Engagement']}
            note="Connect social accounts to track followers, engagement and reel performance."
          />
          <IntegrationCard
            title="Geographic & Predictive Analytics"
            items={['Lead heatmap', 'High-demand areas', 'Forecasted bookings', 'AI suggestions']}
            note="Region heatmaps and forecasting activate once leads carry location data."
          />
        </div>
      </div>
    </div>
  );
};

export default MarketingDashboardTab;
